import moment from 'moment';
import { RAID_METADATA, SEASONS, SEASONS_BY_SLUG } from './enums';
import { DEFAULT_STATISTICS_SEASON_REGION } from './statisticsUtils';
import { TSeasonDef } from '../../../shared/gameConfig/mythicPlus';
import { getKeystoneLeaderboardPeriod } from '../../../shared/common/utils';
import { RAID_METADATA_BY_SLUG } from './enums';
import { TRaidMetadata } from '../../../shared/gameConfig/wowretail/types';

// TODO: Raid and season week related functions are very similar, maybe we can merge them?

export type TSeasonRegionIdentifier = {
  season: string;
  region?: string;
};

export type TRaidRegionIdentifier = {
  raid: string;
  region?: string;
};

export const convertRegionToOffsetRegion = (region?: string): string => {
  if (!region) {
    return 'us';
  }

  return region;
};

/**
 * Returns the week of the season by period
 */
export const getSeasonWeekByPeriod = ({ season, region = '' }: TSeasonRegionIdentifier, period: number): number => {
  const seasonStartPeriod = getKeystoneLeaderboardPeriod(
    convertRegionToOffsetRegion(region),
    SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)]
  );
  return period - seasonStartPeriod + 1;
};

/**
 * Returns the week of the raid by period
 */
export const getRaidWeekByPeriod = ({ raid, region = '' }: TRaidRegionIdentifier, period: number): number => {
  const raidStartPeriod = getKeystoneLeaderboardPeriod(
    convertRegionToOffsetRegion(region),
    RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)]
  );
  return period - raidStartPeriod + 1;
};

/**
 * Returns the start date of the week of the season
 */
export const getSeasonWeekStartDate = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): string => {
  const seasonStart = SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)];
  return seasonStart
    .clone()
    .add(week - 1, 'weeks')
    .toISOString();
};

/**
 * Returns the start date of the week of the raid
 */
export const getRaidWeekStartDate = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): string => {
  const raidStart = RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)];
  return raidStart
    .clone()
    .add(week - 1, 'weeks')
    .toISOString();
};

/**
 * Returns the end date of the week of the season; if the week is ongoing or in the future, the current date is returned.
 */
export const getSeasonWeekEndDate = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): string => {
  const seasonStart = SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)];
  const endDate = seasonStart.clone().add(week, 'weeks');
  const seasonEnd = SEASONS_BY_SLUG.get(season)!.ends[convertRegionToOffsetRegion(region)];

  const maxDate = moment.utc().isBefore(seasonEnd) ? moment.utc() : seasonEnd;

  return maxDate.isBefore(endDate) ? maxDate.toISOString() : endDate.toISOString();
};

/**
 * Returns the end date of the week of the raid;
 * if the week is ongoing or in the future, the current date is returned.
 */
export const getRaidWeekEndDate = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): string => {
  const raidStart = RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)];
  const endDate = raidStart.clone().add(week, 'weeks');
  const raidEnd = RAID_METADATA_BY_SLUG[raid].ends[convertRegionToOffsetRegion(region)];
  const maxDate = moment.utc().isBefore(raidEnd) ? moment.utc() : raidEnd;
  return maxDate.isBefore(endDate) ? maxDate.toISOString() : endDate.toISOString();
};

/**
 * Returns the number of weeks in a season; If the season is ongoing, it will return the number of current week
 */
export const getTotalWeeksOfSeason = ({
  season: seasonSlug,
  region = DEFAULT_STATISTICS_SEASON_REGION
}: TSeasonRegionIdentifier): number => {
  const season = SEASONS_BY_SLUG.get(seasonSlug)!;

  const isSeasonStarted = moment.utc().isAfter(season.starts[convertRegionToOffsetRegion(region)]);

  if (!isSeasonStarted) {
    return 0;
  }

  const seasonEnd = season.ends[convertRegionToOffsetRegion(region)];
  const endDate = moment.utc().isBefore(seasonEnd) ? moment.utc() : seasonEnd;
  const startDate = season.starts[convertRegionToOffsetRegion(region)];
  const differenceInWeeks = endDate.diff(startDate, 'week', true);
  return Math.max(Math.ceil(differenceInWeeks), 1);
};

const calculateWeekByDate = (
  startFirstWeekDate: moment.Moment,
  endLastWeekDate: moment.Moment,
  date: string
): number => {
  const targetDate = moment.utc(date);

  if (!targetDate.isValid()) {
    return 0;
  }

  if (targetDate.isBefore(startFirstWeekDate) || targetDate.isAfter(endLastWeekDate)) {
    return 0;
  }

  const differenceInWeeks = targetDate.diff(startFirstWeekDate, 'week', true);
  return Math.max(Math.ceil(differenceInWeeks), 1);
};

export const getSeasonWeekByDate = (
  { season: seasonSlug, region = '' }: TSeasonRegionIdentifier,
  date: string
): number => {
  const season = SEASONS_BY_SLUG.get(seasonSlug)!;
  const seasonStart = season.starts[convertRegionToOffsetRegion(region)];
  const seasonEnd = season.ends[convertRegionToOffsetRegion(region)];

  return calculateWeekByDate(seasonStart, seasonEnd, date);
};

export const getRaidWeekByDate = ({ raid: raidSlug, region = '' }: TRaidRegionIdentifier, date: string): number => {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  const raidStart = raid.starts[convertRegionToOffsetRegion(region)];
  const raidEnd = raid.ends[convertRegionToOffsetRegion(region)];

  return calculateWeekByDate(raidStart, raidEnd, date);
};

const calculatePeriodRangeByWeeks = (
  firstWeekStartDate: moment.Moment,
  lastWeekEndDate: moment.Moment,
  region: string,
  startWeek: number,
  endWeek: number
): { periodStart: number; periodEnd: number } => {
  const maxDate = moment.utc().isBefore(lastWeekEndDate) ? moment.utc() : lastWeekEndDate;

  let endDate = firstWeekStartDate.clone().add(endWeek - 1, 'weeks');

  if (maxDate.isBefore(endDate)) {
    endDate = maxDate;
  }

  return {
    periodStart: getKeystoneLeaderboardPeriod(
      convertRegionToOffsetRegion(region),
      firstWeekStartDate.clone().add(startWeek - 1, 'weeks')
    ),
    periodEnd: getKeystoneLeaderboardPeriod(convertRegionToOffsetRegion(region), endDate)
  };
};

export const getPeriodRangeBySeasonsWeeks = ({
  season: seasonSlug,
  region = '',
  seasonStartWeek,
  seasonEndWeek
}: {
  seasonStartWeek: number;
  seasonEndWeek: number;
} & TSeasonRegionIdentifier): { periodStart: number; periodEnd: number } => {
  const season = SEASONS_BY_SLUG.get(seasonSlug)!;
  const start = season.starts[convertRegionToOffsetRegion(region)];
  const end = season.ends[convertRegionToOffsetRegion(region)];

  return calculatePeriodRangeByWeeks(start, end, region, seasonStartWeek, seasonEndWeek);
};

export const getPeriodRangeByRaidWeeks = ({
  raid: raidSlug,
  region = '',
  raidStartWeek,
  raidEndWeek
}: {
  raidStartWeek: number;
  raidEndWeek: number;
} & TRaidRegionIdentifier): { periodStart: number; periodEnd: number } => {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  const start = raid.starts[convertRegionToOffsetRegion(region)];
  const end = raid.ends[convertRegionToOffsetRegion(region)];

  return calculatePeriodRangeByWeeks(start, end, region, raidStartWeek, raidEndWeek);
};

/**
 * Returns true if the season is finished
 */
export const checkIsSeasonFinished = ({ season, region = '' }: TSeasonRegionIdentifier): boolean => {
  const now = moment.utc();
  return now.isAfter(SEASONS_BY_SLUG.get(season)!.ends[convertRegionToOffsetRegion(region)]);
};

/**
 * Returns the entire season weeks
 */
export const getEntireSeasonWeeks = ({
  season,
  region = ''
}: TSeasonRegionIdentifier): {
  seasonWeekStart: number;
  seasonWeekEnd: number;
} => {
  return { seasonWeekStart: 1, seasonWeekEnd: getTotalWeeksOfSeason({ season, region }) };
};

export const getEntireRaidWeeks = ({
  raid,
  region = ''
}: TRaidRegionIdentifier): {
  raidWeekStart: number;
  raidWeekEnd: number;
} => {
  return { raidWeekStart: 1, raidWeekEnd: getTotalWeeksOfRaid({ raid, region }) };
};

export const findSeasonByDate = (date: string): TSeasonDef | null => {
  if (!moment(date).isValid()) {
    return null;
  }

  const seasons = SEASONS.filter(
    (season) => moment(date).isSameOrAfter(season.starts.us) && moment(date).isBefore(season.ends.us)
  );

  return seasons.find((season) => season.isMainSeason) || seasons[0] || null;
};

export const isEntireSeasonWeeks = ({
  season,
  region = '',
  seasonWeekStart,
  seasonWeekEnd
}: TSeasonRegionIdentifier & {
  seasonWeekStart: number;
  seasonWeekEnd: number;
}): boolean => {
  return seasonWeekStart === 1 && seasonWeekEnd === getTotalWeeksOfSeason({ season, region });
};

export const getIsSeasonWeekInPast = ({ season, region = '' }: TSeasonRegionIdentifier, week: number): boolean => {
  const seasonStart = SEASONS_BY_SLUG.get(season)!.starts[convertRegionToOffsetRegion(region)];
  const endDate = seasonStart.clone().add(week, 'weeks');
  return moment.utc().isAfter(endDate);
};

export const getIsRaidWeekInPast = ({ raid, region = '' }: TRaidRegionIdentifier, week: number): boolean => {
  const raidStart = RAID_METADATA_BY_SLUG[raid].starts[convertRegionToOffsetRegion(region)];
  const endDate = raidStart.clone().add(week, 'weeks');
  return moment.utc().isAfter(endDate);
};

export const findRaidByDate = (date: string): TRaidMetadata | null => {
  if (!moment(date).isValid()) {
    return null;
  }

  const raids = RAID_METADATA.filter(
    (raid) => moment(date).isSameOrAfter(raid.starts.us) && moment(date).isBefore(raid.ends.us)
  );

  return raids[0];
};

// TODO: looks similar to getTotalWeeksOfSeason, maybe we can merge them?
export const getTotalWeeksOfRaid = ({ raid: raidSlug, region = '' }: TRaidRegionIdentifier): number => {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];

  const isRaidStarted = moment.utc().isAfter(raid.starts[convertRegionToOffsetRegion(region)]);

  if (!isRaidStarted) {
    return 0;
  }

  const raidEnd = raid.ends[convertRegionToOffsetRegion(region)];
  const endDate = moment.utc().isBefore(raidEnd) ? moment.utc() : raidEnd;
  const startDate = raid.starts[convertRegionToOffsetRegion(region)];
  const differenceInWeeks = endDate.diff(startDate, 'week', true);
  return Math.max(Math.ceil(differenceInWeeks), 1);
};
